import React, { useState, useEffect, useRef } from 'react';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { fetch } from '../../utils/api-wrapper';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import './MetabaseDashboard.scss';

const MetabaseDashboard = ({ 
  data,
  dashboardId, 
  params = {}, 
  expirationMinutes = 60 
}) => {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refreshTimerRef = useRef(null);

  const metabaseUrl = process.env.REACT_APP_METABASE_URL || 'https://demo-metabase-618830252369.northamerica-northeast1.run.app';

  // Fonction de récupération du token (extraite pour être réutilisable)
  const fetchToken = () => {
    if (!dashboardId) {
      setError(data.t.read('metabase.missing_id'));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const endpoint = '/metabase/dashboard/token';
    const requestBody = {
      dashboard_id: dashboardId,
      params: params,
      expiration_minutes: expirationMinutes
    };

    fetch(
      endpoint,
      'POST',
      requestBody,
      (response) => {
        const responseData = response.data;
        setToken(responseData.token);

        // Renouvellement automatique du token (5 min avant l'expiration)
        const renewTime = Math.max((responseData.expires_in - 300) * 1000, 60000);
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
        }
        refreshTimerRef.current = setTimeout(fetchToken, renewTime);
        
        setLoading(false);
      },
      (err) => {
        console.error('Erreur lors de la récupération du token Metabase:', err);
        const errorMessage = err?.response?.data?.error 
          || err?.message 
          || data.t.read('metabase.token_fetch_error');
        setError(errorMessage);
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    fetchToken();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
    // On relance si l'ID ou les paramètres changent
  }, [dashboardId, JSON.stringify(params), expirationMinutes]);

  // Affichage du chargement
  if (loading && !token) {
    return (
      <div className='metabase-dashboard-container metabase-loading' data-testid='metabase-loading'>
        <div className='loading-content'>
          <LoadingSpinner />
          <p className='mt-3'>{data.t.read('metabase.loading')}</p>
        </div>
      </div>
    );
  }

  // Affichage de l'erreur
  if (error) {
    return (
      <div className='metabase-dashboard-container metabase-error' data-testid='metabase-error'>
        <div className='error-content'>
          <p className='text-danger mb-3'>{data.t.read('metabase.error')}: {error}</p>
          <button 
            className='btn btn-primary' 
            onClick={fetchToken}
            data-testid='metabase-retry-button'
          >
            {data.t.read('metabase.retry')}
          </button>
        </div>
      </div>
    );
  }

  // Construction de l'URL pour l'iframe
  // Note : On ajoute les options de visibilité via le hash (#)
  const embedUrl = `${metabaseUrl}/embed/dashboard/${token}#bordered=true&titled=true`;

  return (
    <div className='metabase-dashboard-container' style={{ width: '100%', height: 'calc(100vh - 100px)' }}>
      {token && (
        <iframe
          src={embedUrl}
          frameBorder="0"
          width="100%"
          height="100%"
          allowTransparency
          title="Metabase Dashboard"
          className="metabase-iframe"
          style={{ borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
        ></iframe>
      )}
    </div>
  );
};

MetabaseDashboard.propTypes = {
  data: PropTypes.shape({
    t: PropTypes.shape({
      read: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
  dashboardId: PropTypes.number.isRequired,
  params: PropTypes.object,
  expirationMinutes: PropTypes.number,
};

export default inject('data')(observer(MetabaseDashboard));